import { ConfigurationFormData } from '../../types';
import {
  questionIdToFieldName,
  TEXT_INPUT_FIELD_IDS,
  MULTI_DROPDOWN_FIELD_IDS,
  CUSTOM_PLACEHOLDER_IDS,
  QuestionId
} from './const';

export const buildTextInputValidationRules = (
  mandatory: boolean,
  label: string,
  maxValue?: number
) => {
  const rules: any = {};

  if (mandatory) {
    rules.required = `Please enter ${label}`;
  }

  rules.pattern = {
    value: /^[0-9]*$/,
    message: `${label} must be a number`
  };

  if (maxValue !== undefined) {
    rules.validate = (value: any) => {
      if (!value && value !== 0) {
        return true;
      }
      const numValue = Number(value);
      return numValue <= maxValue || `${label} cannot be more than ${maxValue}`;
    };
  }

  return rules;
};

export const getFieldName = (questionId: string): keyof ConfigurationFormData | null => {
  return (questionIdToFieldName[questionId as QuestionId] as keyof ConfigurationFormData) || null;
};

export enum FieldType {
  DPD_EXCLUSION = 'DPD_EXCLUSION',
  DATE = 'DATE',
  DROPDOWN = 'DROPDOWN',
  TEXT_INPUT = 'TEXT_INPUT'
}

export const getFieldType = (questionId: string, options?: string[]): FieldType | null => {
  if (questionId === QuestionId.DPD_EXCLUSIONS) {
    return FieldType.DPD_EXCLUSION;
  }

  if (questionId === QuestionId.ALLOCATION_DATES) {
    return FieldType.DATE;
  }

  if (options && options.length > 0) {
    return FieldType.DROPDOWN;
  }

  if (TEXT_INPUT_FIELD_IDS.includes(questionId as QuestionId)) {
    return FieldType.TEXT_INPUT;
  }

  return null;
};

export const getMaxValue = (questionId: string): number | undefined => {
  const maxValues: { [key: string]: number } = {
    CASE_EXPIRY_DAYS: 31
  };
  return maxValues[questionId as QuestionId];
};

export const getPlaceholder = (questionId: string, isTextInput: boolean): string => {
  if (CUSTOM_PLACEHOLDER_IDS.includes(questionId as QuestionId)) {
    if (isTextInput) {
      return 'Enter here...';
    }
    return 'Select feedback';
  }
  return '';
};

export const showTooltipInfo = (fieldType: FieldType | null): boolean => {
  return fieldType === FieldType.DPD_EXCLUSION;
};

export const getReadableLabel = (value: string): string => {
  return getLabel(value) || value;
};

export const transformOptions = (options: any[]): any[] => {
  if (!options || !Array.isArray(options)) return [];

  return options.map(option => {
    if (typeof option === 'object' && option.label && option.value !== undefined) {
      return option;
    }

    const value = String(option);
    return {
      label: getReadableLabel(value),
      value: value
    };
  });
};

export const isMultiSelectDropdown = (questionId: string): boolean => {
  return MULTI_DROPDOWN_FIELD_IDS.includes(questionId as QuestionId);
};

export const convertToSentenceCase = (text: string): string => {
  const converted = text.toLowerCase().split('_').join(' ');
  return converted.charAt(0).toUpperCase() + converted.slice(1);
};

export const getLabel = (key: string): string => {
  return convertToSentenceCase(key);
};
